import React, { useEffect, useState } from "react";
import Navbar from "../Navbar";
import Footer from "../Footer";
import ContactSection from "../ContactSection";
import Lenis from "@studio-freight/lenis";
import {
  Wallet,
  Receipt,
  Percent,
  CheckCircle,
  FileSpreadsheet,
  Coins,
  ArrowRight,
  LayoutDashboard,
  Users,
  Clock,
  BarChart3
} from "lucide-react";
import "../styles/website.css";
import "../styles/ProductPages.css";

// Import Payroll ERP Screenshots
import dashboardImg from "../../../assets/payroll-dashboard.png";
import employeesImg from "../../../assets/payroll-employees.png";
import attendanceImg from "../../../assets/payroll-attendance.png";
import vaultImg from "../../../assets/payroll-vault.png";
import reportsImg from "../../../assets/payroll-reports.png";

const Payroll = () => {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const isMobile = screenWidth < 900;

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      smoothWheel: true,
      smoothTouch: false,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    return () => {
      lenis.destroy();
    };
  }, []);

  const features = [
    {
      title: "One-Click Salary Run",
      description: "Process monthly salaries, deductions, allowances, and incentives in seconds for different departments.",
      icon: <Coins size={22} />
    },
    {
      title: "Digital Payslips",
      description: "Automatically generate secure, download-ready PDF payslips accessible directly inside employee self-service portals.",
      icon: <Wallet size={22} />
    },
    {
      title: "Automated Tax Calculations",
      description: "Auto-deduct TDS, Professional Tax, and other regional deductions based on latest income tax slabs.",
      icon: <Percent size={22} />
    },
    {
      title: "PF & ESI Compliance",
      description: "Compute employer and employee contributions automatically, ensuring direct submission-ready PF/ESI compliance.",
      icon: <CheckCircle size={22} />
    },
    {
      title: "Claims & Reimbursements",
      description: "Validate employee expense receipts, manage flexible benefit payouts, and process claims in the monthly run.",
      icon: <Receipt size={22} />
    },
    {
      title: "Payroll Analytics Reports",
      description: "Generate structured summaries for monthly payroll costs, department budgets, and bank transfer lists.",
      icon: <FileSpreadsheet size={22} />
    }
  ];

  return (
    <div className="prod-page-wrapper">
      <Navbar />

      {/* HERO */}
      <section className="prod-hero-section">
        <div className="prod-hero-glow"></div>
        <div className="prod-hero-left">
          <span className="prod-badge">FINANCE SUITE</span>
          <h1 className="prod-main-title">
            Automate Payroll. <br />
            <span>Ensure Compliance.</span>
          </h1>
          <p className="prod-desc">
            Simplify salary processing, calculate regional taxes, automate PF/ESI deductions, 
            and deliver digital payslips with zero errors.
          </p>
          <div className="prod-cta-group">
            <a href="#contact" className="prod-primary-btn">
              Request Demo <ArrowRight size={16} />
            </a>
          </div>
        </div>

        {/* HERO VISUAL MOCKUP */}
        <div className="prod-hero-right" style={{ width: "100%", maxWidth: "1000px", zIndex: 10 }}>
          <div className="prod-mockup-frame" style={{
            width: "100%",
            maxWidth: "100%",
            height: "auto",
            padding: isMobile ? "8px" : "12px",
            background: "rgba(10, 10, 10, 0.4)",
            borderColor: "rgba(85, 255, 96, 0.2)",
            boxShadow: "0 25px 80px rgba(85, 255, 96, 0.12)",
            backdropFilter: "blur(20px)"
          }}>
            <div className="mockup-header" style={{ paddingBottom: "8px", marginBottom: "6px" }}>
              <div className="mockup-dots">
                <div className="mockup-dot red"></div>
                <div className="mockup-dot yellow"></div>
                <div className="mockup-dot green"></div>
              </div>
              <span className="mockup-title">payroll.gtelinq.com/dashboard</span>
            </div>
            <div className="mockup-content" style={{ display: "block", overflow: "hidden", padding: 0 }}>
              <img
                src={dashboardImg}
                alt="GTE Payroll ERP Dashboard"
                style={{
                  width: "100%",
                  height: "auto",
                  display: "block",
                  borderRadius: "0 0 12px 12px",
                  imageRendering: "-webkit-optimize-contrast"
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="prod-features-section">
        <div className="prod-section-header">
          <span className="prod-section-mini">PAYROLL ENGINE</span>
          <h2 className="prod-section-title">Error-Free Payroll and Tax Processing</h2>
        </div>
        <div className="prod-features-grid">
          {features.map((feat, idx) => (
            <div className="prod-feature-card" key={idx}>
              <div className="prod-feature-icon">{feat.icon}</div>
              <h4>{feat.title}</h4>
              <p>{feat.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* INTERACTIVE WORKSPACE SHOWCASE */}
      <section className="prod-features-section" style={{ borderTop: "1px solid rgba(255,255,255,0.05)", paddingTop: "80px", paddingBottom: "80px" }}>
        <div className="prod-section-header">
          <span className="prod-section-mini">PRODUCT WORKSPACE</span>
          <h2 className="prod-section-title">Explore the GTE Payroll Dashboard</h2>
        </div>

        <div style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          gap: "40px",
          width: "100%",
          maxWidth: "1200px",
          margin: "0 auto",
          background: "rgba(255,255,255,0.01)",
          border: "1px solid rgba(255,255,255,0.05)",
          borderRadius: "24px",
          padding: isMobile ? "20px" : "40px",
          boxShadow: "0 30px 60px rgba(0,0,0,0.8)",
          backdropFilter: "blur(10px)",
          zIndex: 10,
          position: "relative"
        }}>
          {/* Left Navigation Tabs */}
          <div style={{
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            flex: "1",
            justifyContent: "center"
          }}>
            {[
              { id: "dashboard", title: "Payroll Dashboard", desc: "Overview of monthly disbursements, compliance logs, and cost trend metrics", icon: <LayoutDashboard size={20} /> },
              { id: "employees", title: "Employee Profiles", desc: "Manage detailed staff records, bank info, and dynamic salary profiles", icon: <Users size={20} /> },
              { id: "attendance", title: "Attendance Tracking", desc: "Monitor punch logs, shift timings, locations, and status sync", icon: <Clock size={20} /> },
              { id: "vault", title: "Payroll Vault & Payslips", desc: "Process monthly bulk-runs and generate secure PDF payslips", icon: <Wallet size={20} /> },
              { id: "reports", title: "Reports & Analytics", desc: "Extract salary registers, attendance trends, and custom expenditure charts", icon: <BarChart3 size={20} /> }
            ].map((tab) => {
              const active = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "16px",
                    padding: "20px",
                    background: active ? "rgba(85, 255, 96, 0.08)" : "rgba(255,255,255,0.02)",
                    border: `1px solid ${active ? "rgba(85, 255, 96, 0.25)" : "rgba(255,255,255,0.05)"}`,
                    borderRadius: "16px",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    textAlign: "left",
                    outline: "none"
                  }}
                >
                  <div style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "10px",
                    background: active ? "rgba(85, 255, 96, 0.15)" : "rgba(255,255,255,0.05)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: active ? "#55FF60" : "#fff",
                    transition: "all 0.3s ease",
                    flexShrink: 0
                  }}>
                    {tab.icon}
                  </div>
                  <div>
                    <h4 style={{
                      margin: "0 0 4px 0",
                      fontSize: "16px",
                      color: active ? "#55FF60" : "#fff",
                      fontWeight: "600",
                      transition: "all 0.3s ease"
                    }}>
                      {tab.title}
                    </h4>
                    <span style={{
                      fontSize: "12px",
                      color: "rgba(255,255,255,0.5)"
                    }}>
                      {tab.desc}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Right Display Area */}
          <div style={{
            flex: "1.8",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            background: "rgba(0,0,0,0.4)",
            border: "1px solid rgba(255,255,255,0.05)",
            borderRadius: "20px",
            padding: isMobile ? "15px" : "25px",
            position: "relative",
            minHeight: "450px"
          }}>
            {/* Browser Header Mockup */}
            <div style={{
              width: "100%",
              marginBottom: "15px",
              paddingBottom: "10px",
              borderBottom: "1px solid rgba(255,255,255,0.05)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between"
            }}>
              <div style={{ display: "flex", gap: "6px" }}>
                <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#ff5f56" }}></div>
                <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#ffbd2e" }}></div>
                <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#27c93f" }}></div>
              </div>
              <span style={{
                fontSize: "11px",
                color: "rgba(255,255,255,0.3)",
                fontFamily: "monospace"
              }}>
                {activeTab === "dashboard" && "payroll.gtelinq.com/dashboard"}
                {activeTab === "employees" && "payroll.gtelinq.com/employees"}
                {activeTab === "attendance" && "payroll.gtelinq.com/attendance"}
                {activeTab === "vault" && "payroll.gtelinq.com/vault"}
                {activeTab === "reports" && "payroll.gtelinq.com/reports"}
              </span>
              <div style={{ width: "24px" }}></div>
            </div>

            {/* Display Image inside browser frame */}
            <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", borderRadius: "10px", border: "1px solid rgba(255,255,255,0.05)" }}>
              <img
                src={
                  activeTab === "dashboard" ? dashboardImg :
                    activeTab === "employees" ? employeesImg :
                      activeTab === "attendance" ? attendanceImg :
                        activeTab === "vault" ? vaultImg :
                          reportsImg
                }
                alt="Payroll Panel Showcase"
                style={{
                  width: "100%",
                  height: "auto",
                  display: "block",
                  imageRendering: "-webkit-optimize-contrast"
                }}
              />
            </div>

            {/* Feature Description below image */}
            <div style={{ marginTop: "20px", borderTop: "1px solid rgba(255,255,255,0.05)", paddingTop: "15px" }}>
              {activeTab === "dashboard" && (
                <div>
                  <h3 style={{ color: "#55FF60", margin: "0 0 8px 0", fontSize: "18px" }}>Payroll Management Dashboard</h3>
                  <p style={{ color: "rgba(255,255,255,0.7)", fontSize: "14px", lineHeight: "1.6", margin: 0 }}>
                    Monitor total active employees, monthly payroll costs, upcoming activities, and payslip generation progress from one centralized workspace. The intuitive overview displays interactive metrics for quick analysis.
                  </p>
                </div>
              )}
              {activeTab === "employees" && (
                <div>
                  <h3 style={{ color: "#55FF60", margin: "0 0 8px 0", fontSize: "18px" }}>Detailed Employee Profiles</h3>
                  <p style={{ color: "rgba(255,255,255,0.7)", fontSize: "14px", lineHeight: "1.6", margin: 0 }}>
                    Manage complete records for personal details, employment history, bank details, and payroll breakdowns for every staff member. Role assignments are instantly syncable with the organizational tree.
                  </p>
                </div>
              )}
              {activeTab === "attendance" && (
                <div>
                  <h3 style={{ color: "#55FF60", margin: "0 0 8px 0", fontSize: "18px" }}>Attendance Intelligence Hub</h3>
                  <p style={{ color: "rgba(255,255,255,0.7)", fontSize: "14px", lineHeight: "1.6", margin: 0 }}>
                    Audit punch logs, shift allocations, remote versus office locations, and working hours compliance. Real-time entries automate the computation of salary deductions and attendance payouts.
                  </p>
                </div>
              )}
              {activeTab === "vault" && (
                <div>
                  <h3 style={{ color: "#55FF60", margin: "0 0 8px 0", fontSize: "18px" }}>Secure Payroll Vault & Bulk Generation</h3>
                  <p style={{ color: "rgba(255,255,255,0.7)", fontSize: "14px", lineHeight: "1.6", margin: 0 }}>
                    Distribute monthly payslips securely. Track processed cycles, check outflow allocations, filter records by status (e.g. pending, emailed, downloaded), and trigger bulk salary sheet dispatches instantly.
                  </p>
                </div>
              )}
              {activeTab === "reports" && (
                <div>
                  <h3 style={{ color: "#55FF60", margin: "0 0 8px 0", fontSize: "18px" }}>BI Reports & Funnel Analytics</h3>
                  <p style={{ color: "rgba(255,255,255,0.7)", fontSize: "14px", lineHeight: "1.6", margin: 0 }}>
                    Generate workforce health, compliance, and department metrics reports in one click. View salary distribution curves and export registers to Excel, CSV, or print directly to PDF.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="prod-testimonials-section">
        <div className="prod-section-header">
          <span className="prod-section-mini">TESTIMONIALS</span>
          <h2 className="prod-section-title">Loved by Finance Managers</h2>
        </div>
        <div className="prod-testimonials-grid">
          <div className="prod-testimonial-card">
            <p className="prod-quote">
              "What used to take our finance team three full days now gets completed in 15 minutes. 
              The auto tax slab updates and instant PF calculations are perfect."
            </p>
            <div className="prod-author-info">
              <div className="prod-author-avatar">SM</div>
              <div className="prod-author-meta">
                <h5>Siddharth Mehta</h5>
                <span>Finance Director, Zenith Tech</span>
              </div>
            </div>
          </div>
          <div className="prod-testimonial-card">
            <p className="prod-quote">
              "Reimbursements claim flow is integrated right into payslips, which removes extra processing layers. 
              Compliance reporting has become completely stress-free."
            </p>
            <div className="prod-author-info">
              <div className="prod-author-avatar">KP</div>
              <div className="prod-author-meta">
                <h5>Kirti Patil</h5>
                <span>HR Manager, CloudBase Ltd</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="prod-faq-section">
        <div className="prod-section-header">
          <span className="prod-section-mini">FAQ</span>
          <h2 className="prod-section-title">Frequently Asked Questions</h2>
        </div>
        <div className="prod-faq-list">
          <div className="prod-faq-item">
            <h4>How often are compliance tax rules updated?</h4>
            <p>Our tax tables update automatically in the cloud as soon as government amendments are announced.</p>
          </div>
          <div className="prod-faq-item">
            <h4>Can we define custom salary components?</h4>
            <p>Yes, you can create basic scales, custom HRA configurations, transport allowances, and other custom elements.</p>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <div className="con-page-wrapper">
        <ContactSection />
        <Footer />
      </div>
    </div>
  );
};

export default Payroll;
